import { environment } from '@accredible-frontend-v2/envs';

export interface PostHogUser {
  id: number | string;
  email: string;
  name: string;
}

/**
 * Adds PostHog script to the head of the document
 *
 * Because PostHog is still in an experimental phase, we are using trial accounts which have a limit of one project.
 * So today we have two accounts, one for CourseFinder and one for Dashboard.
 * The following parameters are mainly used to identify the account.
 * For example, the CourseFinder account uses eu.i.posthog.com, while the Dashboard account uses us.i.posthog.com.
 *
 * @param id
 * @param postHogLocation - 'us' | 'eu'
 * @param personProfiles 'identified_only' - only create profiles for identified users | 'always' - create profiles for anonymous as well as identified users
 * @param recordCrossOriginIframes - whether to record cross-origin iframes
 */
export const addPostHog = (
  id: string,
  postHogLocation: 'us' | 'eu',
  personProfiles: 'identified_only' | 'always',
  recordCrossOriginIframes = false,
  testInGreen = false, // TODO(Fred): This parameter is only specified here to allow Shobhna to experiment with PostHog dashboards
): void => {
  if (
    (environment.type === 'production' || (testInGreen && environment.name === 'green')) &&
    location.hostname !== 'localhost'
  ) {
    const scriptTag = document.createElement('script');
    scriptTag.type = 'text/javascript';
    scriptTag.async = true;

    scriptTag.innerHTML = `!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init Ce Os As Te Cs Fs capture Ye calculateEventProperties Ls register register_once register_for_session unregister unregister_for_session qs getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty zs js createPersonProfile Us Rs Bs opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing get_explicit_consent_status is_capturing clear_opt_in_out_capturing Ds debug L Ns getPageViewId captureTraceFeedback captureTraceMetric".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
      posthog.init('${id}', {
          api_host: 'https://${postHogLocation}.i.posthog.com',
          defaults: '2025-05-24',
          person_profiles: '${personProfiles}',
          session_recording: {
            recordCrossOriginIframes: ${recordCrossOriginIframes},
          },
      })`;

    document.head.insertBefore(scriptTag, document.head.firstChild);
  }
};

export const sendPostHogEvent = (eventName: string, value: any): void => {
  const postHog = (<any>window)['posthog'];
  if (postHog) {
    postHog.capture(eventName, { property: value });
  }
};

export const identifyUserToPostHog = (
  user: PostHogUser,
  extraProperties?: Record<string, unknown>,
): void => {
  const postHog = (<any>window)['posthog'];
  if (postHog) {
    postHog.identify(
      user.id, // 'distinct_id'
      { email: user.email, name: user.name, ...extraProperties },
    );
  }
};

export const getPostHogSessionId = (): string => {
  const postHog = (<any>window)['posthog'];
  return postHog ? postHog.get_session_id() : null;
};
